import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { catchError, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { CommonService } from './../../service/common/common.service';
import { NotificationService } from './../../service/notification/notification.service';
import { VmsService } from '../../../modules/vms/vms.service';
import { AdminService } from '../../../modules/admin/admin.service';
import { AccountService } from '../../../modules/account/account.service';
import { ConferenceRoomService } from '../../../modules/conference-room/conference-room.service';
import { TokenService } from '../../service/token/token.service';
import { AuthService } from '../../../auth/auth.service';
import { DataService } from '../../core.index';

type RoutePermissions = {
  '/vms/dashboard': string;
  '/vms/frontdesk': string;
  '/vms/frontdesk/check-in': string,
  '/vms/frontdesk/check-out': string,
  '/vms/invitations': string;
  '/vms/visitors': string;
  '/vms/reports/feedback-report': string;
  '/vms/reports/visitor-report': string;
  '/vms/reports/pending-checkout-report': string;
  '/vms/reports/force-checkout-report': string,
  '/vms/reports/time-spent-report': string,
  '/admin/users': string;
  '/admin/roles-permissions': string;
  '/admin/organizations': string;
  '/admin/desks': string;
  // '/account': string;
  '/admin/amenities': string;
  '/admin/designation': string;
  '/grievance/dashboard': string;
  '/grievance/settings': string;
  '/grievance/grievance-report': string;
  '/grievance/escalation-report': string;
  '/expense/dashboard': string;
  '/expense/my-trip': string;
  '/expense/travel-desk': string;
  '/expense/trip-approvals': string;
  '/expense/claim-expense-approvals': string;
  '/expense/settings/expense-types': string;
  '/expense/settings/expense-approval-matrix': string;
  '/expense/settings/gl-master': string;
  '/expense/settings/cost-center': string;
  '/expense/settings/project-base': string;
  '/expense/settings/travel-policy': string;
  '/expense/settings/travel-desk-master': string;
  '/expense/reports/trip-history': string;
  '/expense/reports/trip-roster': string;
  '/expense/reports/claim-summary': string;
  '/conference-room/dashboard': string;
  '/conference-room/my-bookings': string;
  '/conference-room/room-display': string;
  '/conference-room/approvals': string;
  '/conference-room/reports/booking-summary': string;
  '/conference-room/reports/room-utilisation': string;
  '/conference-room/reports/approval-tracking': string;
  '/conference-room/settings/amenities': string;
  '/conference-room/settings/conference-rooms': string;
  '/approval-notes/dashboard'?: string;
  '/approval-notes/budget-approval': string;
  '/approval-notes/vendor-ticket': string;
  '/approval-notes/vendor-selection': string;
  '/approval-notes/scrap-approval': string;
  '/approval-notes/vendor-creation': string;
  '/approval-notes/payment-approval': string;
  '/approval-notes/vendors': string;
};

interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: string;
  refresh_token?: string;
}

@Injectable({
  providedIn: 'root',
})

export class ModuleGuard implements CanActivate {
  private isProcessingToken = false;
  orgUnits: any[] = [];

  constructor(
    private adminService: AdminService,
    private commonService: CommonService,
    private accountService: AccountService,
    private conferenceRoomService: ConferenceRoomService,
    private tokenService: TokenService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private vmsService: VmsService,
    private dataService: DataService,
    private router: Router
  ) { }


  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {


    const modulePath = route.url.length > 0 ? route.url[0].path : '/';
    const fullPath = state.url;

    const url = new URL(window.location.href);
    const code: string = url.searchParams.get('code') || '';
    const iframe = url.searchParams.get('iframe') || 'false';
    localStorage.setItem('iframe', iframe);

    if (code && !this.isProcessingToken) {
      return this.handleLoginWithCode(code);
    }

    // Check if user is logged in, if not store the intended URL and redirect to login
    if (!this.tokenService.isLoggedIn()) {
      if (fullPath !== '/' && fullPath !== '') {
        localStorage.setItem('returnUrl', fullPath);
      }
      this.router.navigate(['/']);
      return false;
    }

    return this.accountService.getProfile().pipe(
      tap((data) => {
        if (data) {
          const currentProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
          const serverRoles = (data as any)?.roles;
          const updatedProfile = {
            ...currentProfile,
            ...data,
            roles: serverRoles
              ? {
                  ...(currentProfile?.roles || {}),
                  ...serverRoles,
                  auth_items: serverRoles.auth_items || [],
                  module_names: serverRoles.module_names || []
                }
              : currentProfile?.roles
          };
          localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
          this.commonService.setProfile(updatedProfile as any);
          this.commonService.refreshMenuItems();
        }
      }),
      catchError((err) => {
        console.error('ModuleGuard: Error refreshing user profile:', err);
        return of(null);
      }),
      switchMap(() => {
        return of(this.evaluateAccess(modulePath, fullPath));
      })
    );
  }

  private evaluateAccess(modulePath: string, fullPath: string): boolean {
    // Redirect to default module route if only the base path is accessed
    if (fullPath === '/admin' || fullPath === '/vms' || fullPath === '/conference-room' || fullPath === '/approval-notes') {
      const defaultRoute = this.getDefaultModuleRoute(modulePath);
      if (defaultRoute) {
        this.router.navigate([defaultRoute]);
        return false;
      } else {
        this.router.navigate([this.commonService.getDefaultRoute()]);
        return false;
      }
    }
    if (!modulePath) {
      this.router.navigate(['/']);
      return false;
    }

    // Allow access if user is an admin
    if (this.commonService.isAdmin()) {
      this.commonService.canAccessModule(modulePath);
      // Load module-specific sidebar data
      switch (modulePath) {
        case 'vms':
          this.vmsService.getSidebarData();
          break;
        case 'admin':
          this.adminService.getSidebarData();
          break;
        case 'conference-room':
          this.conferenceRoomService.getSidebarData();
          break;
      }
      return true;
    }

    // Check if user has permission to access this module
    const hasAccess = this.commonService.canAccessModule(modulePath);


    if (!hasAccess) {
      const userProfile = localStorage.getItem('userProfile');

      // if (userProfile) {
      //   this.notificationService.showNotification('danger', 'Access denied!');
      // }

      // Redirect to the default route if access is denied
      // Clear returnUrl if user doesn't have access to the requested module
      localStorage.removeItem('returnUrl');
      this.router.navigate([this.commonService.getDefaultRoute()]);
      return false;
    }


    // 2. Then check specific route access
    const hasRouteAccess = this.checkRouteAccess(fullPath);
    if (!hasRouteAccess) {
      const userProfile = localStorage.getItem('userProfile');
      // if (userProfile) {
      //   this.notificationService.showNotification('danger', 'Access denied!');
      // }

      // Clear returnUrl if user doesn't have access to the specific route
      localStorage.removeItem('returnUrl');
      // Redirect to the first accessible route within the module
      const defaultRoute = this.getDefaultModuleRoute(modulePath);
      if (defaultRoute) {
        this.router.navigate([defaultRoute]);
      } else {
        this.router.navigate([this.commonService.getDefaultRoute()]);
      }
      return false;
    }

    // Load module-specific sidebar data
    switch (modulePath) {
      case 'vms':
        this.vmsService.getSidebarData();
        break;
      case 'admin':
        this.adminService.getSidebarData();
        break;
      case 'conference-room':
        this.conferenceRoomService.getSidebarData();
        break;
    }

    return true;
  }



  private checkRouteAccess(fullPath: string): boolean {
    if (fullPath === '/apps') {
      return true;
    }
    const permissions = this.commonService.getUserPermissions();
    // Grant full access if the user is an admin
    const routePermissionMap: RoutePermissions = {
      '/vms/dashboard': '',
      '/vms/frontdesk': 'vms-frontdesk',
      '/vms/frontdesk/check-in': 'vms-frontdesk',
      '/vms/frontdesk/check-out': 'vms-frontdesk',
      '/vms/invitations': 'vms-invitation',
      '/vms/visitors': 'vms-visitors',
      '/admin/users': 'admin-users',
      '/admin/roles-permissions': 'admin-roles',
      '/admin/organizations': 'admin-orgunit',
      '/admin/desks': 'admin-desk',
      // '/account' : 'account-myaccount',
      '/admin/amenities': 'admin-amenities',
      '/admin/designation': 'admin-designation',
      '/grievance/dashboard': 'grievance-approval',
      '/grievance/settings': 'grievance-settings',
      '/grievance/grievance-report': 'grievance-report',
      '/grievance/escalation-report': 'escalation-report',
      '/vms/reports/feedback-report': 'vms-feedbackreports',
      '/vms/reports/visitor-report': 'vms-visitorreport',
      '/vms/reports/pending-checkout-report': 'vms-checkoutreport',
      '/vms/reports/force-checkout-report': 'vms-force-out-report',
      '/vms/reports/time-spent-report': 'vms-timespentreport',
      '/expense/dashboard': '',
      '/expense/my-trip': 'expense-my-trip',
      '/expense/travel-desk': 'expense-travel-desk',
      '/expense/trip-approvals': 'expense-approvals',
      '/expense/claim-expense-approvals': 'expense-claim-expense-approval',
      '/expense/settings/expense-types': 'expense-settings',
      '/expense/settings/expense-approval-matrix': 'expense-settings',
      '/expense/settings/gl-master': 'expense-settings',
      '/expense/settings/cost-center': 'expense-settings',
      '/expense/settings/travel-policy': 'expense-settings',
      '/expense/settings/project-base': 'expense-settings',
      '/expense/settings/travel-desk-master': 'expense-settings',
      '/expense/reports/trip-history': 'expense-trip-history',
      '/expense/reports/trip-roster': 'expense-trip-roster',
      '/expense/reports/claim-summary': 'expense-claimsummary',
      '/conference-room/dashboard': '',
      '/conference-room/my-bookings': '',
      '/conference-room/room-display': 'cr-room-display',
      '/conference-room/approvals': 'cr-approvals',
      '/conference-room/reports/room-utilisation': 'cr-reports',
      '/conference-room/reports/approval-tracking': 'cr-reports',
      '/conference-room/reports/booking-summary': 'cr-reports',
      '/conference-room/settings/amenities': 'cr-amenities',
      '/conference-room/settings/conference-rooms': 'cr-conference-room-master',
      '/approval-notes/dashboard': '',
      '/approval-notes/budget-approval': 'approval-notes-budget-approval-display',
      '/approval-notes/vendor-ticket': 'approval-notes-vendor-ticket-display',
      '/approval-notes/vendor-selection': 'approval-notes-vendor-selection-display',
      '/approval-notes/scrap-approval': 'approval-notes-scrap-approval-display',
      '/approval-notes/vendor-creation': 'approval-notes-vendor-creation-display',
      '/approval-notes/payment-approval': 'approval-notes-payment-initiate-display',
      '/approval-notes/vendors': 'approval-notes-vendors-display',
    };

    // Type assertion since we know we're checking against defined routes
    const requiredPermission = routePermissionMap[fullPath as keyof RoutePermissions];
    if (!requiredPermission) return true; // Change: return true if no permission required (e.g. dashboard)
    return permissions.includes(requiredPermission);
  }

  private getDefaultModuleRoute(modulePath: string): string | null {
    const permissions = this.commonService.getUserPermissions();

    const moduleRoutes: { [key: string]: { permission: string; route: string }[] } = {
      'vms': [
        { permission: 'vms-dashboard', route: '/vms/dashboard' },
        { permission: 'vms-frontdesk', route: '/vms/frontdesk' },
        { permission: 'vms-frontdesk', route: '/vms/frontdesk/check-in' },
        { permission: 'vms-invitation', route: '/vms/invitations' },
        { permission: 'vms-visitors', route: '/vms/visitors' },
        { permission: 'vms-feedbackreports', route: '/vms/reports/feedback-report' },
        { permission: 'vms-visitorreport', route: '/vms/reports/visitor-report' },
        { permission: 'vms-checkoutreport', route: '/vms/reports/pending-checkout-report' },
        { permission: 'vms-force-out-report', route: '/vms/reports/force-checkout-report' },
        { permission: 'vms-timespentreport', route: '/vms/reports/time-spent-report' },
      ],
      'admin': [
        { permission: 'admin-users', route: '/admin/users' },
        { permission: 'admin-roles', route: '/admin/roles-permissions' },
        { permission: 'admin-orgunit', route: '/admin/organizations' },
        { permission: 'admin-desk', route: '/admin/desks' },
        { permission: 'admin-amenities', route: '/admin/amenities' },
        { permission: 'admin-conferences', route: '/admin/conferences' },
        { permission: 'admin-setting', route: '/admin/setting' }
      ],
      'grievance': [
        { permission: 'grievance-approval', route: '/grievance/dashboard' },
        { permission: 'grievance-approval', route: '/grievance/request' },
        { permission: 'grievance-settings', route: '/grievance/settings' },
        { permission: 'grievance-report', route: '/grievance/grievance-report' },
        { permission: 'escalation-report', route: '/grievance/escalation-report' },
      ],
      'expense': [
        { permission: 'expense-dashboard', route: '/expense/dashboard' },
        { permission: 'expense-my-trip', route: '/expense/my-trip' },
        { permission: 'expense-travel-desk', route: '/expense/travel-desk' },
        { permission: 'expense-approvals', route: '/expense/trip-approvals' },
        { permission: 'expense-claim-expense-approval', route: '/expense/claim-expense-approvals' },
        { permission: 'expense-settings', route: '/expense/settings/expense-types' },
        { permission: 'expense-settings', route: '/expense/settings/expense-approval-matrix' },
        { permission: 'expense-settings', route: '/expense/settings/gl-master' },
        { permission: 'expense-settings', route: '/expense/settings/cost-center' },
        { permission: 'expense-settings', route: '/expense/settings/travel-policy' },
        { permission: 'expense-settings', route: '/expense/settings/project-base' },
        { permission: 'expense-settings', route: '/expense/settings/travel-desk-master' },
        { permission: 'expense-trip-history', route: '/expense/reports/trip-history' },
        { permission: 'expense-trip-roster', route: '/expense/reports/trip-roster' },
        { permission: 'expense-claimsummary', route: '/expense/reports/claim-summary' },
      ],
      'conference-room': [
        { permission: '', route: '/conference-room/dashboard' },
        { permission: '', route: '/conference-room/my-bookings' },
        { permission: 'cr-room-display', route: '/conference-room/room-display' },
        { permission: 'cr-approvals', route: '/conference-room/approvals' },
        { permission: 'cr-reports', route: '/conference-room/reports/room-utilisation' },
        { permission: 'cr-reports', route: '/conference-room/reports/approval-tracking' },
        { permission: 'cr-reports', route: '/conference-room/reports/booking-summary' },
        { permission: 'cr-amenities', route: '/conference-room/settings/amenities' },
        { permission: 'cr-conference-room-master', route: '/conference-room/settings/conference-rooms' },
      ],
      'approval-notes': [
        { permission: '', route: '/approval-notes/dashboard' },
        { permission: 'approval-notes-budget-approval-display', route: '/approval-notes/budget-approval' },
        { permission: 'approval-notes-vendor-ticket-display', route: '/approval-notes/vendor-ticket' },
        { permission: 'approval-notes-vendor-selection-display', route: '/approval-notes/vendor-selection' },
        { permission: 'approval-notes-scrap-approval-display', route: '/approval-notes/scrap-approval' },
        { permission: 'approval-notes-vendor-creation-display', route: '/approval-notes/vendor-creation' },
        { permission: 'approval-notes-payment-initiate-display', route: '/approval-notes/payment-approval' },
        { permission: 'approval-notes-vendors-display', route: '/approval-notes/vendors' },
      ]
    };

    if (modulePath === 'vms') return '/vms/dashboard';
    if (modulePath === 'expense') return '/expense/dashboard';
    if (modulePath === 'conference-room') return '/conference-room/dashboard';
    if (modulePath === 'approval-notes') return '/approval-notes/dashboard';

    const routes = moduleRoutes[modulePath];
    if (!routes) return null;

    // Find the first route the user has permission to access
    const firstAccessibleRoute = routes.find(route =>
      permissions.includes(route.permission)
    );

    return firstAccessibleRoute ? firstAccessibleRoute.route : null;
  }

  private handleLoginWithCode(code: string): Observable<boolean> {
    this.isProcessingToken = true;
    this.removeCodeFromUrl();
    this.tokenService.removeTokens();
    localStorage.removeItem('userProfile');
    localStorage.removeItem('orgUnits');
    localStorage.removeItem('access_token');
    localStorage.removeItem('REFRESH_TOKEN');
    localStorage.removeItem('TOKEN_TYPE');
    localStorage.removeItem('EXPIRE_IN');

    return this.authService.loginWithCode(code).pipe(
      switchMap((response: any) => {
        const tokenData: LoginResponse = {
          access_token: response.access_token || response.accessToken || code,
          token_type: response.token_type || response.tokenType || 'Bearer',
          expires_in: response.expires_in || response.expiresIn || '3600',
          refresh_token: response.refresh_token || response.refreshToken
        };

        this.tokenService.storeTokens(tokenData);

        return this.accountService.getProfile().pipe(
          tap((profileData) => {
            localStorage.setItem('userProfile', JSON.stringify(profileData));
            this.orgUnits = profileData.org_units || [];
            this.dataService.setOrgUnits(this.orgUnits);
            localStorage.setItem('orgUnits', JSON.stringify(this.orgUnits));

            // if (window.location.pathname.includes('/vms')) {
            //   this.router.navigate(['/vms/dashboard']);
            // } else if (!window.location.pathname.includes('/vms/admin')) {
            //   console.log('Admin module detected');
            //   const defaultRoute = this.commonService.canAccessModule('admin');
            //   if (defaultRoute) {
            //     this.router.navigate([this.commonService.getDefaultRoute()]);
            //   } else {
            //     console.log('No default route found for admin module');
            //     this.router.navigate(['/code-login-error']);
            //   }
            //   // this.router.navigate([this.commonService.getDefaultRoute()]);
            // }

            if (window.location.pathname.includes('/admin')) {
              const defaultRoute = this.commonService.canAccessModule('admin');
              if (defaultRoute) {
                // this.router.navigate([this.commonService.getDefaultRoute()]);
                this.router.navigate(['/admin/users']);
              } else {
                this.router.navigate(['/code-login-error']);
              }
            } else if (window.location.pathname.includes('/vms')) {
              this.router.navigate(['/vms/dashboard']);
            } else if (window.location.pathname.includes('/expense')) {
              this.router.navigate(['/expense/dashboard']);
            }
          }),
          switchMap(() => of(false)) // Prevent route activation
        );
      }),
      catchError((error) => {
        console.error('Login error:', error);
        this.router.navigate(['/code-login-error']);
        return of(false);
      }),
      finalize(() => {
        this.isProcessingToken = false;
      })
    );
  }

  private removeCodeFromUrl(): void {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      window.history.replaceState({}, document.title, url.toString());
    }
  }




}
